import { useCallback, useEffect, useMemo, useState, memo } from 'react'
import { useDebounce } from '@/hooks/debounce';
import Table from "@/components/Table/Table";
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '@/store/store';
import { getUserGeofence, getGeofenceReport, addUserGeofences, removeUserGeofences, getUserGeofenceAreaById } from '@/store/slices/GeofenceManagementSlice';
import type { RootState } from '@/store/rootReducer';
import { Button, TextField, Tooltip } from "@mui/material";
import FilterComponent from "@/pages/FilterComponent/FilterComponent";
import { getDepartmentList, getRolesList, getDesignationList } from '@/store/slices/userSlice';
import AppliedFilter from '../FilterComponent/AppliedFilter';
import { ButtonLoader } from '@/components/Loader/ButtonLoader';
import { getGeoFencingArea } from "@/store/slices/teamSlice";
import ManageGeofence from './ManageGeofence';
import UserGeofences from './UserGeofences';
import ManageSelectedUsersGeofences from './ManageSelectedUsersGeofences';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ViewLocation from '@/components/ViewLocation/ViewLocation';
import { getGeoFenceType } from "@/store/slices/teamSlice";
import toast from '@/lib/toast';
import GeofenceLogs from './GeofenceLogs';
import { useDrawer } from "@/components/Drawer/context/DrawerContext";
import type { showFilters } from '../FilterComponent/FilterInterface';
import GeoFencePills from './GeoFencePills';

type SelectionMode = "NONE" | "PAGE" | "ALL";

const GeofenceView = () => {

    const [modalOpen, setModalOpen] = useState(false);
    const [assignGeofenceModalOpen, setAssignGeofenceModalOpen] = useState(false);

    const [assignGeofenceState, setAssignGeofenceState] = useState<"add" | "remove" | null>(null);

    const [selectedUser, setSelectedUser] = useState<any>(null);

    const [selectedIds, setSelectedIds] = useState<any>([]);

    const [SelectionMode, setSelectionMode] = useState<SelectionMode>("NONE"); //to hnadle pages selection
    const applyToAllUsers = SelectionMode === "ALL";

    const [userGeofenceModalOpen, setUserGeofenceModalOpen] = useState(false);

    const [modalOpenViewLocation, setModalOpenViewLocation] = useState(false);
    const [location, setLocation] = useState({ lat: "", lng: "" });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebounce((searchText || "").trim());
    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const [filterData, setFilterData] = useState<any>({
        departmentIds: null,
        designationIds: null,
        roleIds: null,
        punchInType: null,
        punchOutType: null,
        punchInName: null,
        punchOutName: null,
        userGeofenceStatus: null,
        geofenceTypeId: null
    });
    const { userGeofence, loading } = useSelector((state: RootState) => state.usergeofenceManagement);
    const { roleList, departmentList, designationList } = useSelector((state: RootState) => state.user);
    const { geoFencingArea } = useSelector((state: any) => state.team);
    const { geoFenceType } = useSelector((state: any) => state.team);
    const dispatch = useDispatch<AppDispatch>();
    const { openDrawer } = useDrawer();


    // constants/geofenceOptions.ts
    const userGeofenceStatusList = [
        {
            value: "punchInAssigned",
            label: "Punch In - Geofence Assigned",
        },
        {
            value: "punchInNotAssigned",
            label: "Punch In - Geofence Not Assigned",
        },
        {
            value: "punchOutAssigned",
            label: "Punch Out - Geofence Assigned",
        },
        {
            value: "punchOutNotAssigned",
            label: "Punch Out - Geofence Not Assigned",
        },
    ];
    const handleOpenManageModal = (file: any) => {
        setSelectedUser(file);
        setModalOpen(true);
    }

    //view location 
    const handleOnClickLocation = useCallback(
        async (item: any) => {
            // item can be an object with Id or Name or Latitude/Longitude
            const geofenceId = item?.Id;
            const geofenceName = item?.Name;

            try {
                if (geofenceId) {
                    const localArea = geoFencingArea?.find((g: any) => g?.Id === geofenceId);
                    if (localArea && localArea.Latitude != null && localArea.Longitude != null) {
                        setLocation({ lat: String(localArea.Latitude), lng: String(localArea.Longitude) });
                        setModalOpenViewLocation(true);
                        return;
                    }

                    // fallback to id-based API only if local data is missing
                    const area: any = await dispatch(getUserGeofenceAreaById(geofenceId)).unwrap();
                    if (area && area.Latitude != null && area.Longitude != null) {
                        setLocation({ lat: String(area.Latitude), lng: String(area.Longitude) });
                        setModalOpenViewLocation(true);
                        return;
                    }
                }

                // fallback to name lookup in preloaded list
                if (geofenceName) {
                    const found = geoFencingArea?.find((g: any) => g?.Name?.toString().toLowerCase() === geofenceName.toString().toLowerCase());
                    if (found && found.Latitude != null && found.Longitude != null) {
                        setLocation({ lat: String(found.Latitude), lng: String(found.Longitude) });
                        setModalOpenViewLocation(true);
                        return;
                    }
                }

                // Fallback to coordinates present on the item itself
                if (item?.Latitude != null && item?.Longitude != null) {
                    setLocation({ lat: String(item.Latitude), lng: String(item.Longitude) });
                    setModalOpenViewLocation(true);
                    return;
                }
                toast.error("Coordinates not available for this geofence");
            } catch (err: any) {               
                toast.error("Failed to fetch geofence details");
            }
        },
        [geoFencingArea, dispatch]
    );

    const queryParams = useMemo(() => ({
        from: (page - 1) * rowsPerPage + 1,
        to: page * rowsPerPage,
        searchText: debouncedSearch,
        departmentIds: filterData?.departmentIds?.map((d: any) => d.Id)?.join(",") || "",
        designationIds: filterData?.designationIds?.map((d: any) => d.Id)?.join(",") || "",
        roleIds: filterData?.roleIds?.map((d: any) => d.Id)?.join(",") || "",
        PunchInGeoFenceIds: filterData?.punchInName?.map((d: any) => d.Id)?.join(",") || "",
        PunchOutGeoFenceIds: filterData?.punchOutName?.map((d: any) => d.Id)?.join(",") || "",
        punchInGeoFenceTypeIds: filterData?.punchInType?.map((d: any) => d.Id)?.join(",") || "",
        punchOutGeoFenceTypeIds: filterData?.punchOutType?.map((d: any) => d.Id)?.join(",") || "",
        punchInAssigned:
            filterData?.userGeofenceStatus?.value === "punchInAssigned" ? true : filterData?.userGeofenceStatus?.value === "punchInNotAssigned"
                ? false : null,
        punchOutAssigned:
            filterData?.userGeofenceStatus?.value === "punchOutAssigned" ? true : filterData?.userGeofenceStatus?.value === "punchOutNotAssigned"
                ? false : null,
        geofenceTypeId: filterData?.geofenceTypeId?.value || "",
    }), [
        page,
        rowsPerPage,
        debouncedSearch,
        filterData.departmentIds,
        filterData.designationIds,
        filterData.roleIds,
        filterData.punchInName,
        filterData.punchOutName,
        filterData.punchInType,
        filterData.punchOutType,
        filterData.userGeofenceStatus,
        filterData.geofenceTypeId,
    ]);

    // Prepare the active filters to be sent to the API for the 'Select All' feature
    const activeFilters = useMemo(() => ({
        searchText: debouncedSearch,
        departmentIds: filterData?.departmentIds?.map((d: any) => d.Id)?.join(",") || "",
        designationIds: filterData?.designationIds?.map((d: any) => d.Id)?.join(",") || "",
        roleIds: filterData?.roleIds?.map((d: any) => d.Id)?.join(",") || "",
        PunchInGeoFenceIds: filterData?.punchInName?.map((d: any) => d.Id)?.join(",") || "",
        PunchOutGeoFenceIds: filterData?.punchOutName?.map((d: any) => d.Id)?.join(",") || "",
        punchInGeoFenceTypeIds: filterData?.punchInType?.map((d: any) => d.Id)?.join(",") || "",
        punchOutGeoFenceTypeIds: filterData?.punchOutType?.map((d: any) => d.Id)?.join(",") || "",
        punchInAssigned:
            filterData?.userGeofenceStatus?.value === "punchInAssigned" ? true : filterData?.userGeofenceStatus?.value === "punchInNotAssigned"
                ? false : null,
        punchOutAssigned:
            filterData?.userGeofenceStatus?.value === "punchOutAssigned" ? true : filterData?.userGeofenceStatus?.value === "punchOutNotAssigned"
                ? false : null,
        geofenceTypeId: filterData?.geofenceTypeId?.value || "",
    }), [
        debouncedSearch,
        filterData.departmentIds,
        filterData.designationIds,
        filterData.roleIds,
        filterData.punchInName,
        filterData.punchOutName,
        filterData.punchInType,
        filterData.punchOutType,
        filterData.userGeofenceStatus,
        filterData.geofenceTypeId,
    ]);

    const handleOpenUserGeofencesModal = (user: any) => {
        setSelectedUser(user);
        setUserGeofenceModalOpen(true);
    }

    const applyFilter = useCallback((data: any) => {
        setFilterData(data);
        setPage(1);
        setFilterAnchor(null);
    }, []);

    const clearFilter = useCallback((data: any) => {
        setFilterData(data);
        setPage(1);
    }, []);

    const handleExport = useCallback(() => {
        const exportParams = { ...queryParams, from: 1, to: -1 };
        dispatch(getGeofenceReport(exportParams));
    }, [dispatch, queryParams]);

    const handleAssignSelectedUsersGeofences = () => {
        setAssignGeofenceModalOpen(true);
    }

    const handleAuditClick = () => {
        openDrawer(<GeofenceLogs />);
    };

    const onSelectionChange = (ids: any) => {
        setSelectedIds(ids);
        if (ids.length === 0) {
            setSelectionMode("NONE");
        } else {
            setSelectionMode("PAGE");
        }
    };

    const handlePageChange = useCallback((page: number) => {
        setPage(page);

        if (SelectionMode === "PAGE") {
            setSelectedIds([]);
            setSelectionMode("NONE");
        }
    }, [SelectionMode]);

    useEffect(() => {
        if (
            SelectionMode === "ALL" &&
            userGeofence?.Items?.length
        ) {
            setSelectedIds(
                userGeofence.Items.map((x: any) => x.UserId)
            );
        }
    }, [userGeofence?.Items?.length, SelectionMode]);

    useEffect(() => {
        setPage(1);
        setSelectedIds([]);
        setSelectionMode("NONE");
    }, [debouncedSearch, filterData]);

    useEffect(() => {
        dispatch(getDepartmentList({ from: 1, to: -1, text: "" }));
        dispatch(getRolesList());
        dispatch(getDesignationList({ from: 1, to: -1, text: "" }));
        dispatch(getGeoFencingArea());
        dispatch(getGeoFenceType());
        // dispatch(getUserGeofenceLogs());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getUserGeofence(queryParams));
    }, [dispatch, queryParams]);

    // checks automatically Select All Users when all users on the current page are selected
    const userItems = userGeofence?.Items || [];

    const handleAddUserGeofences = async (arg: any) => {
        try {
            // Single-user edit modal
            if (arg?.addPayload || arg?.removePayload) {
                if (arg.removePayload?.GeoFences?.length > 0) {
                    await dispatch(removeUserGeofences(arg.removePayload)).unwrap();
                    toast.success("User geofence removed successfully.");
                }

                if (arg.addPayload?.GeoFences?.length > 0) {
                    await dispatch(addUserGeofences(arg.addPayload)).unwrap();
                    toast.success("User geofence assigned successfully.");
                }
            }

            // Bulk assign/remove
            if (arg && (arg.mode === "add" || arg.mode === "remove")) {                
                if (arg.mode === "add" && arg.payload) {                 
                    if (arg.mode === "add" && arg.payload) {
                        await dispatch(addUserGeofences(arg.payload)).unwrap();
                        toast.success("Geofences assigned successfully.");
                    }                                    
                } else if (arg.mode === "remove" && arg.payload) {
                    await dispatch(removeUserGeofences(arg.payload)).unwrap();
                    toast.success("Geofences removed successfully.");
                }
            }
            dispatch(getUserGeofence(queryParams));
        } catch (error: any) {
            toast.error(error || "Failed to update geofences. Please try again.");
        } finally {
            setModalOpen(false);
            setAssignGeofenceModalOpen(false);
            setSelectedUser(null);
            setSelectedIds([]);
            setSelectionMode("NONE");
        }
    };

    const columns = useMemo(
        () => [
            {
                name: "User Name",
                selector: (file: any) => file?.UserName || "-",
                truncateLimit: 15,
                width: "150px",
            },
            {
                name: "Emp No",
                selector: (file: any) => file?.EmployeeNo || "-",
                width: "100px",
            },
            {
                name: "Designation",
                selector: (file: any) => file?.DesignationName || "-",
                width: "110px",
            },
            {
                name: "Department",
                selector: (file: any) => file?.DepartmentName || "-",
                width: "110px",
            },
            {
                name: "Role",
                selector: (file: any) => file?.RoleName || "-",
                width: "110px",
            },
            {
                name: <span style={{ whiteSpace: "nowrap" }}>Punch In Geofences</span>,
                selector: (file: any) => file?.PunchInGeoence || "-",
                width: "200px",
                customRender: (file: any) => (
                    <GeoFencePills
                        value={file?.PunchInGeoFences}
                        color="green"
                        handleOpenUserGeofencesModal={() => handleOpenUserGeofencesModal(file)}
                        handleOnClickLocation={handleOnClickLocation}
                    />
                ),
            },
            {
                name: <span style={{ whiteSpace: "nowrap" }}>Punch Out Geofences</span>,
                selector: (file: any) => file?.PunchOutGeoFence || "_",
                width: "200px",
                customRender: (file: any) => (
                    <GeoFencePills
                        value={file?.PunchOutGeoFences}
                        color="orange"
                        handleOpenUserGeofencesModal={() => handleOpenUserGeofencesModal(file)}
                        handleOnClickLocation={handleOnClickLocation}
                    />
                ),
            },
            {
                name: "",
                selector: (file: any) => file?.UserId,
                width: "90px",
                customRender: (file: any) => (
                    <Tooltip title="Manage Geofence">
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={selectedIds.length > 1} //> 0 change here 
                                className="w-10! min-w-10! h-10 rounded-full! bg-transparent transition-all duration-200 ease-in-out hover:text-neutral-900! hover:border-neutral-500! hover:bg-neutral-900/10 hover:scale-105 active:scale-95 text-sm font-bold"
                                onClick={() => handleOpenManageModal(file)}
                            >
                                +
                            </Button>
                        </span>
                    </Tooltip>
                ),
            }
        ], [handleOnClickLocation, handleOpenManageModal, handleOpenUserGeofencesModal]
    );

    const showFilters: showFilters = {
        role: { show: true, mode: "multi" },
        designation: { show: true, mode: "multi" },
        department: { show: true, mode: "multi" },
        geoFenceType: { show: true, mode: "multi" },
        geoFenceName: { show: true, mode: "multi" },
        userGeofenceStatus: { show: true, mode: "single" },
    };

    return (
        <div className='p-4'>
            <div className="mb-5">
                <div className='flex items-center justify-between border-b border-solid border-gray-200 mb-4 pb-4 pt-4'>
                    {/* Left Side: Filter */}
                    <div>
                        <Tooltip title="Filter">
                            <Button variant="contained" onClick={(e) => setFilterAnchor(e.currentTarget)}>Filter</Button>
                        </Tooltip>

                        <FilterComponent
                            anchorEl={filterAnchor}
                            onClose={() => setFilterAnchor(null)}
                            onApply={applyFilter}
                            appliedFilter={filterData}
                            filterName="GeofenceManagement"
                            width="500px"
                            showFilters={showFilters}
                            list={{
                                roleList: roleList || [],
                                designationList: designationList?.Items || [],
                                departmentList: departmentList?.Items || [],
                                geoFenceList: geoFenceType || [],
                                geoFenceNameList: geoFencingArea || [],
                                userGeofenceStatusList: userGeofenceStatusList || [],
                            }}
                        />
                    </div>

                    {/* Right Side */}
                    <div className="flex items-center gap-4" >
                        {
                            selectedIds.length > 1 && (
                                <>
                                    <Button variant="outlined" color="primary" className="mr-2"
                                        onClick={() => {
                                            setAssignGeofenceState("add");
                                            handleAssignSelectedUsersGeofences();
                                        }}
                                        sx={{ fontSize: "12px", padding: "4px 10px", textTransform: "none" }}
                                    >
                                        <PersonAddIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        Add Geofences
                                    </Button>
                                    <Button variant="outlined" color="error"
                                        onClick={() => {
                                            setAssignGeofenceState("remove");
                                            handleAssignSelectedUsersGeofences();
                                        }}
                                        sx={{ fontSize: "12px", padding: "4px 10px", textTransform: "none" }}
                                    >
                                        <RemoveCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        Remove Geofences
                                    </Button>
                                </>
                            )

                        }
                        <TextField
                            placeholder="Search"
                            variant="standard"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-90"
                        />
                        <ButtonLoader
                            variant="contained"
                            onClick={handleExport}
                            loading={loading.geofenceExport}
                            sx={{ fontSize: "12px", padding: "4px 10px", textTransform: "none" }}
                            size="small"
                        >
                            Export
                        </ButtonLoader>
                        <Button variant='contained' onClick={handleAuditClick}
                            sx={{ fontSize: "12px", padding: "4px 10px", textTransform: "none" }}
                        >
                            Geofence Logs
                        </Button>

                    </div>
                </div>

                <AppliedFilter filterData={filterData} onClearFilter={clearFilter} showFilters={showFilters} />
            </div>

            {!applyToAllUsers && selectedIds.length === userItems.length && userItems.length > 0 && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        marginBottom: "16px",
                        background: "#e3f2fd",
                        borderRadius: "4px",
                    }}
                >
                    <InfoIcon
                        sx={{
                            color: "#1976d2",
                            fontSize: 20,
                            flexShrink: 0,
                        }}
                    />

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                            fontSize: "11px",
                            lineHeight: "18px",
                        }}
                    >
                        <span>All <strong>{userItems.length}</strong> users on this page are selected.</span>

                        <button
                            type="button"
                            onClick={() => setSelectionMode("ALL")}
                            style={{
                                border: "none",
                                background: "none",
                                color: "#1976d2",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: 600,
                                padding: 0,
                            }}
                        >
                            Select all {userGeofence?.TotalRecords} users
                        </button>
                    </div>
                </div>
            )}

            {applyToAllUsers && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        marginBottom: "16px",
                        background: "#e8f5e9",
                        borderRadius: "4px",
                    }}
                >
                    <CheckCircleOutlineIcon
                        sx={{
                            color: "#2e7d32",
                            fontSize: 20,
                            flexShrink: 0,
                        }}
                    />

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                            fontSize: "11px",
                            lineHeight: "18px",
                        }}
                    >
                        <span> All <strong>{userGeofence?.TotalRecords}</strong> users are selected. </span>

                        <button
                            type="button"
                            onClick={() => {
                                setSelectionMode("NONE");
                                setSelectedIds([]);
                            }}
                            style={{
                                border: "none",
                                background: "none",
                                color: "#1976d2",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: 600,
                                padding: 0,
                            }}
                        >
                            Clear selection
                        </button>
                    </div>
                </div>
            )}

            <Table
                columns={columns}
                data={userGeofence?.Items || []}
                showCheckbox={true}
                checkboxWidth='30px'
                rowKey="UserId"
                onSelectionChange={onSelectionChange}
                selectedRows={selectedIds}
                paginationProps={{
                    isPagination: true,
                    totalCount: userGeofence?.TotalRecords || 0,
                    pageSelected: page,
                    setPageSelected: handlePageChange,
                    rowsPerPageValue: rowsPerPage,
                    setRowsPerPageValue: setRowsPerPage,
                }}
                loading={loading?.userGeofence}
            />

            <ManageGeofence
                modalOpen={modalOpen}
                closeModal={() => {
                    setModalOpen(false);
                    setSelectedUser(null);
                }}
                saveModal={handleAddUserGeofences}
                selectedUser={selectedUser}
            />

            <UserGeofences
                UserGeofencesModalOpen={userGeofenceModalOpen}
                UserGeofencesCloseModal={() => {
                    setUserGeofenceModalOpen(false);
                    setSelectedUser(null);
                }}
                selectedUser={selectedUser}
                geoFencingArea={geoFencingArea}
            />

            <ManageSelectedUsersGeofences
                modalOpen={assignGeofenceModalOpen}
                closeModal={() => setAssignGeofenceModalOpen(false)}
                saveModal={handleAddUserGeofences}
                selectedIds={selectedIds}
                mode={assignGeofenceState || "add"}                
                usersList={userGeofence?.Items || []}
                applyToAllUsers={applyToAllUsers}
                filters={activeFilters}
            />

            {modalOpenViewLocation && (
                <ViewLocation
                    title="Location"
                    width="800px"
                    modalOpen={modalOpenViewLocation}
                    closeModal={() => setModalOpenViewLocation(false)}
                    lat={location.lat}
                    lng={location.lng}
                />
            )}
        </div>
    );
}

export default memo(GeofenceView);
